import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Users, DollarSign, TrendingUp, Calendar, CheckCircle, 
  Send, Loader2, Instagram, Facebook, Twitter, Twitch,
  Sparkles, ArrowRight, Heart, Globe, LogIn
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const AffiliatesPage = () => {
  const { currentLanguage, t: tg } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    twitch: '',
    twitter_x: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/affiliates/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.detail || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      title: 'Affiliate Program',
      subtitle: 'Partner with PenLab Peptide and earn up to 20% commission on every sale',
      heroTagline: 'Join Our Team',
      whyJoin: 'Why Become an Affiliate?',
      benefit1Title: 'Up to 20% Commission',
      benefit1Desc: 'Start at 10% and unlock higher tiers as you grow',
      benefit2Title: 'Monthly Payouts',
      benefit2Desc: 'Receive your earnings via bank transfer every month',
      benefit3Title: 'Premium Products',
      benefit3Desc: 'Promote high-quality, in-demand research peptides',
      benefit4Title: 'Dedicated Support',
      benefit4Desc: 'Get personalized assistance to maximize your earnings',
      howItWorks: 'How It Works',
      step1: 'Apply using the form below',
      step2: 'We review your application (24-48 hours)',
      step3: 'Receive your unique promo code',
      step4: 'Share with your audience and start earning!',
      cookieTitle: '90-Day Tracking Cookie',
      cookieDesc: 'When a customer clicks your affiliate link, our tracking cookie stays active for 90 days. This means you earn commission on any purchase they make within this period, even if they don\'t buy immediately!',
      cookieDays: 'Days of Tracking',
      cookieSubtext: 'Industry-leading cookie duration',
      commissionTitle: 'Tiered Commission Structure',
      commissionDesc: 'The more you sell, the more you earn! Our tiered structure rewards your success with increasing commission rates.',
      sales: 'sales',
      applyTitle: 'Apply Now',
      applySubtitle: 'Fill out the form below to join our affiliate program',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      socialPlatforms: 'Social Media Platforms',
      message: 'Tell us about yourself',
      messagePlaceholder: 'Tell us about your audience, content niche, and why you want to promote PenLab Peptide...',
      submit: 'Submit Application',
      successTitle: 'Application Submitted!',
      successMsg: 'Thank you for applying to our affiliate program. We\'ll review your application and get back to you within 24-48 hours.',
      optional: 'optional'
    },
    es: {
      title: 'Programa de Afiliados',
      subtitle: 'Asóciate con PenLab Peptide y gana hasta 20% de comisión en cada venta',
      heroTagline: 'Únete a Nuestro Equipo',
      whyJoin: '¿Por qué ser Afiliado?',
      benefit1Title: 'Hasta 20% Comisión',
      benefit1Desc: 'Comienza con 10% y desbloquea niveles más altos a medida que creces',
      benefit2Title: 'Pagos Mensuales',
      benefit2Desc: 'Recibe tus ganancias por transferencia bancaria cada mes',
      benefit3Title: 'Productos Premium',
      benefit3Desc: 'Promociona péptidos de investigación de alta calidad y demanda',
      benefit4Title: 'Soporte Dedicado',
      benefit4Desc: 'Obtén asistencia personalizada para maximizar tus ganancias',
      howItWorks: 'Cómo Funciona',
      step1: 'Aplica usando el formulario de abajo',
      step2: 'Revisamos tu solicitud (24-48 horas)',
      step3: 'Recibe tu código promocional único',
      step4: '¡Comparte con tu audiencia y comienza a ganar!',
      cookieTitle: 'Cookie de Seguimiento de 90 Días',
      cookieDesc: 'Cuando un cliente hace clic en tu enlace de afiliado, nuestra cookie de seguimiento permanece activa durante 90 días. ¡Esto significa que ganas comisión en cualquier compra que realicen durante este período, incluso si no compran inmediatamente!',
      cookieDays: 'Días de Seguimiento',
      cookieSubtext: 'Duración líder en la industria',
      commissionTitle: 'Estructura de Comisión por Niveles',
      commissionDesc: '¡Cuanto más vendas, más ganas! Nuestra estructura por niveles recompensa tu éxito con tasas de comisión crecientes.',
      sales: 'ventas',
      applyTitle: 'Aplica Ahora',
      applySubtitle: 'Completa el formulario para unirte a nuestro programa de afiliados',
      name: 'Nombre Completo',
      email: 'Correo Electrónico',
      phone: 'Número de Teléfono',
      socialPlatforms: 'Redes Sociales',
      message: 'Cuéntanos sobre ti',
      messagePlaceholder: 'Cuéntanos sobre tu audiencia, nicho de contenido, y por qué quieres promocionar PenLab Peptide...',
      submit: 'Enviar Solicitud',
      successTitle: '¡Solicitud Enviada!',
      successMsg: 'Gracias por aplicar a nuestro programa de afiliados. Revisaremos tu solicitud y te contactaremos en 24-48 horas.',
      optional: 'opcional'
    },
    ru: {
      title: 'Партнёрская Программа',
      subtitle: 'Станьте партнёром PenLab Peptide и зарабатывайте до 20% комиссии с каждой продажи',
      heroTagline: 'Присоединяйтесь к Команде',
      whyJoin: 'Почему стать партнёром?',
      benefit1Title: 'До 20% Комиссия',
      benefit1Desc: 'Начните с 10% и открывайте более высокие уровни по мере роста',
      benefit2Title: 'Ежемесячные Выплаты',
      benefit2Desc: 'Получайте заработок банковским переводом каждый месяц',
      benefit3Title: 'Премиум Продукты',
      benefit3Desc: 'Продвигайте качественные исследовательские пептиды',
      benefit4Title: 'Персональная Поддержка',
      benefit4Desc: 'Получите персонализированную помощь для максимизации заработка',
      howItWorks: 'Как Это Работает',
      step1: 'Подайте заявку через форму ниже',
      step2: 'Мы рассмотрим вашу заявку (24-48 часов)',
      step3: 'Получите ваш уникальный промокод',
      step4: 'Делитесь с аудиторией и начинайте зарабатывать!',
      cookieTitle: '90-Дневный Отслеживающий Cookie',
      cookieDesc: 'Когда клиент нажимает на вашу партнёрскую ссылку, наш отслеживающий cookie остаётся активным 90 дней. Это означает, что вы получаете комиссию с любой покупки, сделанной в этот период, даже если они не покупают сразу!',
      cookieDays: 'Дней Отслеживания',
      cookieSubtext: 'Лидирующая в отрасли длительность',
      commissionTitle: 'Многоуровневая Структура Комиссий',
      commissionDesc: 'Чем больше продаёте, тем больше зарабатываете! Наша многоуровневая структура вознаграждает ваш успех растущими ставками комиссий.',
      sales: 'продаж',
      applyTitle: 'Подать Заявку',
      applySubtitle: 'Заполните форму, чтобы присоединиться к нашей партнёрской программе',
      name: 'Полное Имя',
      email: 'Электронная Почта',
      phone: 'Номер Телефона',
      socialPlatforms: 'Социальные Сети',
      message: 'Расскажите о себе',
      messagePlaceholder: 'Расскажите о вашей аудитории, нише контента, и почему вы хотите продвигать PenLab Peptide...',
      submit: 'Отправить Заявку',
      successTitle: 'Заявка Отправлена!',
      successMsg: 'Спасибо за заявку в нашу партнёрскую программу. Мы рассмотрим её и свяжемся с вами в течение 24-48 часов.',
      optional: 'опционально'
    },
    de: {
      title: 'Partnerprogramm',
      subtitle: 'Arbeiten Sie mit PenLab Peptide zusammen und verdienen Sie bis zu 20 % Provision auf jeden Verkauf',
      heroTagline: 'Werden Sie Teil unseres Teams',
      whyJoin: 'Warum Affiliate werden?',
      benefit1Title: 'Bis zu 20 % Provision',
      benefit1Desc: 'Starten Sie mit 10 % und schalten Sie höhere Stufen frei, während Sie wachsen',
      benefit2Title: 'Monatliche Auszahlungen',
      benefit2Desc: 'Erhalten Sie Ihre Einnahmen jeden Monat per Banküberweisung',
      benefit3Title: 'Premium-Produkte',
      benefit3Desc: 'Bewerben Sie hochwertige, gefragte Forschungspeptide',
      benefit4Title: 'Engagierter Support',
      benefit4Desc: 'Erhalten Sie personalisierte Unterstützung, um Ihre Einnahmen zu maximieren',
      howItWorks: 'So funktioniert es',
      step1: 'Bewerben Sie sich über das Formular unten',
      step2: 'Wir prüfen Ihre Bewerbung (24-48 Stunden)',
      step3: 'Erhalten Sie Ihren einzigartigen Promo-Code',
      step4: 'Teilen Sie ihn mit Ihrem Publikum und beginnen Sie zu verdienen!',
      cookieTitle: '90-tägiger Tracking-Cookie',
      cookieDesc: 'Wenn ein Kunde auf Ihren Affiliate-Link klickt, bleibt unser Tracking-Cookie 90 Tage lang aktiv. Das bedeutet, dass Sie Provision auf jeden Kauf verdienen, den er in diesem Zeitraum tätigt, selbst wenn er nicht sofort kauft!',
      cookieDays: 'Tage Tracking',
      cookieSubtext: 'Branch führende Cookie-Dauer',
      commissionTitle: 'Gestaffelte Provisionsstruktur',
      commissionDesc: 'Je mehr Sie verkaufen, desto mehr verdienen Sie! Unsere gestaffelte Struktur belohnt Ihren Erfolg mit steigenden Provisionssätzen.',
      sales: 'Verkäufe',
      applyTitle: 'Jetzt bewerben',
      applySubtitle: 'Füllen Sie das Formular unten aus, um unserem Partnerprogramm beizutreten',
      name: 'Vollständiger Name',
      email: 'E‑Mail-Adresse',
      phone: 'Telefonnummer',
      socialPlatforms: 'Soziale Medien Plattformen',
      message: 'Erzählen Sie uns etwas über sich',
      messagePlaceholder: 'Erzählen Sie uns von Ihrem Publikum, Ihrer Content-Nische und warum Sie PenLab Peptide bewerben möchten...',
      submit: 'Bewerbung absenden',
      successTitle: 'Bewerbung eingereicht!',
      successMsg: 'Vielen Dank für Ihre Bewerbung für unser Partnerprogramm. Wir werden Ihre Bewerbung prüfen und uns innerhalb von 24-48 Stunden bei Ihnen melden.',
      optional: 'optional'
    },
    fr: {
      title: 'Programme d’affiliation',
      subtitle: 'Devenez partenaire de PenLab Peptide et gagnez jusqu’à 20 % de commission sur chaque vente',
      heroTagline: 'Rejoignez notre équipe',
      whyJoin: 'Pourquoi devenir affilié ?',
      benefit1Title: 'Jusqu’à 20 % de commission',
      benefit1Desc: 'Commencez à 10 % et débloquez des paliers supérieurs au fur et à mesure de votre croissance',
      benefit2Title: 'Paiements mensuels',
      benefit2Desc: 'Recevez vos gains par virement bancaire chaque mois',
      benefit3Title: 'Produits premium',
      benefit3Desc: 'Faites la promotion de peptides de recherche de haute qualité et très demandés',
      benefit4Title: 'Support dédié',
      benefit4Desc: 'Bénéficiez d’un accompagnement personnalisé pour maximiser vos gains',
      howItWorks: 'Comment ça marche',
      step1: 'Postulez en utilisant le formulaire ci-dessous',
      step2: 'Nous examinons votre candidature (24 à 48 heures)',
      step3: 'Recevez votre code promotionnel unique',
      step4: 'Partagez-le avec votre audience et commencez à gagner !',
      cookieTitle: 'Cookie de suivi de 90 jours',
      cookieDesc: 'Lorsqu’un client clique sur votre lien d’affiliation, notre cookie de suivi reste actif pendant 90 jours. Cela signifie que vous touchez une commission sur tout achat effectué durant cette période, même s’il n’achète pas immédiatement !',
      cookieDays: 'Jours de suivi',
      cookieSubtext: 'Durée de cookie parmi les meilleures du secteur',
      commissionTitle: 'Structure de commission par paliers',
      commissionDesc: 'Plus vous vendez, plus vous gagnez ! Notre structure par paliers récompense votre succès avec des taux de commission croissants.',
      sales: 'ventes',
      applyTitle: 'Postuler maintenant',
      applySubtitle: 'Remplissez le formulaire ci-dessous pour rejoindre notre programme d’affiliation',
      name: 'Nom complet',
      email: 'Adresse e-mail',
      phone: 'Numéro de téléphone',
      socialPlatforms: 'Plateformes de réseaux sociaux',
      message: 'Parlez-nous de vous',
      messagePlaceholder: 'Parlez-nous de votre audience, de votre niche de contenu et de la raison pour laquelle vous souhaitez promouvoir PenLab Peptide...',
      submit: 'Envoyer la candidature',
      successTitle: 'Candidature envoyée !',
      successMsg: 'Merci d’avoir postulé à notre programme d’affiliation. Nous examinerons votre candidature et reviendrons vers vous sous 24 à 48 heures.',
      optional: 'facultatif'
    },
    it: {
      title: 'Programma di Affiliazione',
      subtitle: 'Collabora con PenLab Peptide e guadagna fino al 20% di commissione su ogni vendita',
      heroTagline: 'Unisciti al nostro team',
      whyJoin: 'Perché diventare affiliato?',
      benefit1Title: 'Fino al 20% di commissione',
      benefit1Desc: 'Parti dal 10% e sblocca livelli superiori man mano che cresci',
      benefit2Title: 'Pagamenti mensili',
      benefit2Desc: 'Ricevi i tuoi guadagni tramite bonifico bancario ogni mese',
      benefit3Title: 'Prodotti premium',
      benefit3Desc: 'Promuovi peptidi di ricerca di alta qualità e ad alta richiesta',
      benefit4Title: 'Supporto dedicato',
      benefit4Desc: 'Ottieni assistenza personalizzata per massimizzare i tuoi guadagni',
      howItWorks: 'Come funziona',
      step1: 'Fai domanda utilizzando il modulo sottostante',
      step2: 'Esaminiamo la tua candidatura (24-48 ore)',
      step3: 'Ricevi il tuo codice promozionale univoco',
      step4: 'Condividilo con il tuo pubblico e inizia a guadagnare!',
      cookieTitle: 'Cookie di tracciamento di 90 giorni',
      cookieDesc: 'Quando un cliente clicca sul tuo link di affiliazione, il nostro cookie di tracciamento rimane attivo per 90 giorni. Questo significa che guadagni una commissione su qualsiasi acquisto che effettua entro questo periodo, anche se non compra immediatamente!',
      cookieDays: 'Giorni di tracciamento',
      cookieSubtext: 'Durata del cookie leader del settore',
      commissionTitle: 'Struttura di commissioni a livelli',
      commissionDesc: 'Più vendi, più guadagni! La nostra struttura a livelli premia il tuo successo con tassi di commissione crescenti.',
      sales: 'vendite',
      applyTitle: 'Candidati ora',
      applySubtitle: 'Compila il modulo sottostante per entrare nel nostro programma di affiliazione',
      name: 'Nome e cognome',
      email: 'Indirizzo email',
      phone: 'Numero di telefono',
      socialPlatforms: 'Piattaforme di social media',
      message: 'Parlaci di te',
      messagePlaceholder: 'Parlaci del tuo pubblico, della nicchia dei tuoi contenuti e del motivo per cui vuoi promuovere PenLab Peptide...',
      submit: 'Invia candidatura',
      successTitle: 'Candidatura inviata!',
      successMsg: 'Grazie per aver fatto domanda per il nostro programma di affiliazione. Esamineremo la tua candidatura e ti risponderemo entro 24-48 ore.',
      optional: 'opzionale'
    },
    pl: {
      title: 'Program partnerski',
      subtitle: 'Współpracuj z PenLab Peptide i zarabiaj do 20% prowizji od każdej sprzedaży',
      heroTagline: 'Dołącz do naszego zespołu',
      whyJoin: 'Dlaczego warto zostać partnerem?',
      benefit1Title: 'Do 20% prowizji',
      benefit1Desc: 'Zacznij od 10% i odblokowuj wyższe progi w miarę wzrostu',
      benefit2Title: 'Miesięczne wypłaty',
      benefit2Desc: 'Otrzymuj swoje zarobki co miesiąc przelewem bankowym',
      benefit3Title: 'Produkty premium',
      benefit3Desc: 'Promuj wysokiej jakości, poszukiwane peptydy badawcze',
      benefit4Title: 'Dedykowane wsparcie',
      benefit4Desc: 'Uzyskaj spersonalizowaną pomoc, aby zmaksymalizować swoje zarobki',
      howItWorks: 'Jak to działa',
      step1: 'Złóż wniosek za pomocą formularza poniżej',
      step2: 'Przeanalizujemy Twoje zgłoszenie (24-48 godzin)',
      step3: 'Otrzymasz swój unikalny kod promocyjny',
      step4: 'Udostępnij go swojej publiczności i zacznij zarabiać!',
      cookieTitle: '90-dniowe ciasteczko śledzące',
      cookieDesc: 'Gdy klient kliknie Twój link partnerski, nasze ciasteczko śledzące pozostaje aktywne przez 90 dni. Oznacza to, że otrzymasz prowizję od każdego zakupu dokonanego w tym okresie, nawet jeśli nie kupią od razu!',
      cookieDays: 'Dni śledzenia',
      cookieSubtext: 'Wiodący w branży czas trwania ciasteczka',
      commissionTitle: 'Warstwowa struktura prowizji',
      commissionDesc: 'Im więcej sprzedajesz, tym więcej zarabiasz! Nasza warstwowa struktura nagradza Twój sukces rosnącymi stawkami prowizji.',
      sales: 'sprzedaż',
      applyTitle: 'Aplikuj teraz',
      applySubtitle: 'Wypełnij formularz poniżej, aby dołączyć do naszego programu partnerskiego',
      name: 'Imię i nazwisko',
      email: 'Adres e-mail',
      phone: 'Numer telefonu',
      socialPlatforms: 'Platformy mediów społecznościowych',
      message: 'Opowiedz nam o sobie',
      messagePlaceholder: 'Opowiedz nam o swojej publiczności, niszy tematycznej treści i dlaczego chcesz promować PenLab Peptide...',
      submit: 'Wyślij zgłoszenie',
      successTitle: 'Zgłoszenie wysłane!',
      successMsg: 'Dziękujemy za zgłoszenie do naszego programu partnerskiego. Przejrzymy Twoje zgłoszenie i skontaktujemy się z Tobą w ciągu 24-48 godzin.',
      optional: 'opcjonalne'
    },
    nl: {
      title: 'Affiliateprogramma',
      subtitle: 'Werk samen met PenLab Peptide en verdien tot 20% commissie op elke verkoop',
      heroTagline: 'Word Lid van Ons Team',
      whyJoin: 'Waarom Affiliate Worden?',
      benefit1Title: 'Tot 20% Commissie',
      benefit1Desc: 'Begin bij 10% en ontgrendel hogere niveaus naarmate je groeit',
      benefit2Title: 'Maandelijkse Uitbetalingen',
      benefit2Desc: 'Ontvang je verdiensten elke maand via bankoverschrijving',
      benefit3Title: 'Premium Producten',
      benefit3Desc: 'Promoot hoogwaardige, veelgevraagde onderzoekspeptiden',
      benefit4Title: 'Toegewijde Ondersteuning',
      benefit4Desc: 'Krijg persoonlijke ondersteuning om je verdiensten te maximaliseren',
      howItWorks: 'Hoe Het Werkt',
      step1: 'Meld je aan met het onderstaande formulier',
      step2: 'Wij beoordelen je aanvraag (24-48 uur)',
      step3: 'Ontvang je unieke promotiecode',
      step4: 'Deel met je publiek en begin met verdienen!',
      cookieTitle: 'Trackingcookie van 90 Dagen',
      cookieDesc: 'Wanneer een klant op jouw affiliate link klikt, blijft onze trackingcookie 90 dagen actief. Dit betekent dat je commissie verdient op elke aankoop die zij in deze periode doen, zelfs als ze niet direct kopen!',
      cookieDays: 'Dagen Tracking',
      cookieSubtext: 'Toonaangevende cookieduur in de sector',
      commissionTitle: 'Gelaagde Commissiestructuur',
      commissionDesc: 'Hoe meer je verkoopt, hoe meer je verdient! Onze gelaagde structuur beloont je succes met oplopende commissietarieven.',
      sales: 'verkopen',
      applyTitle: 'Nu Aanmelden',
      applySubtitle: 'Vul het onderstaande formulier in om deel te nemen aan ons affiliateprogramma',
      name: 'Volledige Naam',
      email: 'E-mailadres',
      phone: 'Telefoonnummer',
      socialPlatforms: 'Socialmediaplatformen',
      message: 'Vertel ons iets over jezelf',
      messagePlaceholder: 'Vertel ons over je publiek, jouw contentniche en waarom je PenLab Peptide wilt promoten...',
      submit: 'Aanvraag Indienen',
      successTitle: 'Aanvraag Ingediend!',
      successMsg: 'Bedankt voor je aanmelding voor ons affiliateprogramma. We beoordelen je aanvraag en nemen binnen 24-48 uur contact met je op.',
      optional: 'optioneel'
    },
    pt: {
      title: 'Programa de Afiliados',
      subtitle: 'Faça parceria com a PenLab Peptide e ganhe até 20% de comissão em cada venda',
      heroTagline: 'Junte-se à Nossa Equipe',
      whyJoin: 'Por que se Tornar um Afiliado?',
      benefit1Title: 'Até 20% de Comissão',
      benefit1Desc: 'Comece com 10% e desbloqueie níveis mais altos à medida que você cresce',
      benefit2Title: 'Pagamentos Mensais',
      benefit2Desc: 'Receba seus ganhos via transferência bancária todos os meses',
      benefit3Title: 'Produtos Premium',
      benefit3Desc: 'Promova peptídeos de pesquisa de alta qualidade e alta demanda',
      benefit4Title: 'Suporte Dedicado',
      benefit4Desc: 'Obtenha assistência personalizada para maximizar seus ganhos',
      howItWorks: 'Como Funciona',
      step1: 'Inscreva-se usando o formulário abaixo',
      step2: 'Nós analisamos sua inscrição (24-48 horas)',
      step3: 'Receba seu código promocional exclusivo',
      step4: 'Compartilhe com seu público e comece a ganhar!',
      cookieTitle: 'Cookie de Rastreamento de 90 Dias',
      cookieDesc: 'Quando um cliente clica no seu link de afiliado, nosso cookie de rastreamento permanece ativo por 90 dias. Isso significa que você ganha comissão sobre qualquer compra que ele fizer dentro desse período, mesmo que não compre imediatamente!',
      cookieDays: 'Dias de Rastreamento',
      cookieSubtext: 'Duração de cookie líder do setor',
      commissionTitle: 'Estrutura de Comissão em Níveis',
      commissionDesc: 'Quanto mais você vende, mais você ganha! Nossa estrutura em níveis recompensa seu sucesso com taxas de comissão crescentes.',
      sales: 'vendas',
      applyTitle: 'Inscreva-se Agora',
      applySubtitle: 'Preencha o formulário abaixo para entrar no nosso programa de afiliados',
      name: 'Nome Completo',
      email: 'Endereço de Email',
      phone: 'Número de Telefone',
      socialPlatforms: 'Plataformas de Mídia Social',
      message: 'Fale sobre você',
      messagePlaceholder: 'Fale sobre seu público, nicho de conteúdo e por que você quer promover a PenLab Peptide...',
      submit: 'Enviar Inscrição',
      successTitle: 'Inscrição Enviada!',
      successMsg: 'Obrigado por se inscrever no nosso programa de afiliados. Vamos analisar sua inscrição e entraremos em contato em até 24-48 horas.',
      optional: 'opcional'
    },
  };

  const t = translations[currentLanguage] || translations.en;

  const benefits = [
    { icon: DollarSign, title: t.benefit1Title, desc: t.benefit1Desc, color: 'from-green-500 to-emerald-500' },
    { icon: Calendar, title: t.benefit2Title, desc: t.benefit2Desc, color: 'from-capsule-teal to-capsule-aqua' },
    { icon: TrendingUp, title: t.benefit3Title, desc: t.benefit3Desc, color: 'from-blue-500 to-indigo-500' },
    { icon: Sparkles, title: t.benefit4Title, desc: t.benefit4Desc, color: 'from-capsule-salmon to-capsule-rose-gold' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <Card className="max-w-lg mx-auto shadow-xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="text-center py-12 px-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.successTitle}</h2>
              <p className="text-gray-600 mb-6">{t.successMsg}</p>
              <Link to="/affiliate/login">
                <Button className="bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white rounded-2xl"
                  data-testid="success-go-to-login">
                  <LogIn className="w-4 h-4 mr-2" />
                  {currentLanguage === 'es' ? 'Ir al Portal de Afiliados' :
                   currentLanguage === 'ru' ? 'Перейти в портал партнёра' :
                   'Go to Affiliate Portal'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="affiliateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#affiliateGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#affiliateGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-24 h-24 rounded-full bg-gradient-to-br from-green-500/30 to-emerald-500/20 blur-xl"></div>
        <div className="absolute bottom-10 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <Users className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t.heroTagline}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t.title}</h1>
            <p className="text-xl text-gray-300 mb-6">{t.subtitle}</p>
            
            {/* Commission Badge + Existing affiliate login CTA */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-3 rounded-full shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
                <span className="text-white font-bold text-xl">Up to 20% Commission</span>
              </div>
              <Link to="/affiliate/login" data-testid="affiliate-login-cta-hero">
                <span className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white px-5 py-3 rounded-full border border-white/30 transition-all">
                  <LogIn className="w-4 h-4" />
                  {currentLanguage === 'es' ? '¿Ya eres afiliado? Iniciar sesión' :
                   currentLanguage === 'ru' ? 'Уже партнёр? Войти' :
                   "Already an affiliate? Log in"}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12">
        {/* Benefits Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t.whyJoin}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="shadow-lg border-0 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t.howItWorks}</h2>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-capsule-teal to-capsule-aqua hidden md:block"></div>
              
              {[t.step1, t.step2, t.step3, t.step4].map((step, index) => (
                <div key={index} className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-full flex items-center justify-center flex-shrink-0 shadow-lg z-10">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-md flex-1">
                    <p className="text-gray-700">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 90-Day Cookie & Tiered Commission Section */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {/* 90-Day Cookie Card */}
            <Card className="shadow-lg border-0 rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <span className="text-2xl">🍪</span>
                  {t.cookieTitle || '90-Day Tracking Cookie'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {t.cookieDesc || 'When a customer clicks your affiliate link, our tracking cookie stays active for 90 days. This means you earn commission on any purchase they make within this period, even if they don\'t buy immediately!'}
                </p>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                      90
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{t.cookieDays || 'Days of Tracking'}</p>
                      <p className="text-xs text-gray-500">{t.cookieSubtext || 'Industry-leading cookie duration'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tiered Commission Card */}
            <Card className="shadow-lg border-0 rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5" />
                  {t.commissionTitle || 'Tiered Commission Structure'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {t.commissionDesc || 'The more you sell, the more you earn! Our tiered structure rewards your success with increasing commission rates.'}
                </p>
                <div className="space-y-3">
                  {/* Tier 1 */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">1</span>
                      <span className="text-gray-700 font-medium">1-10 {t.sales || 'sales'}</span>
                    </div>
                    <span className="text-green-600 font-bold text-lg">10%</span>
                  </div>
                  {/* Tier 2 */}
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm">2</span>
                      <span className="text-gray-700 font-medium">11-30 {t.sales || 'sales'}</span>
                    </div>
                    <span className="text-emerald-600 font-bold text-lg">15%</span>
                  </div>
                  {/* Tier 3 */}
                  <div className="flex items-center justify-between p-3 bg-teal-50 rounded-xl border border-teal-100">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold text-sm">3</span>
                      <span className="text-gray-700 font-medium">31+ {t.sales || 'sales'}</span>
                    </div>
                    <span className="text-teal-600 font-bold text-lg">20%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Application Form Section */}
        <section className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0 rounded-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Send className="w-5 h-5" />
                {t.applyTitle}
              </CardTitle>
              <p className="text-white/90 text-sm mt-1">{t.applySubtitle}</p>
            </CardHeader>
            <CardContent className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">{t.name} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-xl"
                    data-testid="affiliate-name-input"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rounded-xl"
                    data-testid="affiliate-email-input"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">{t.phone} <span className="text-gray-400 text-xs">({t.optional})</span></Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="rounded-xl"
                  />
                </div>

                {/* Social Platforms */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-capsule-teal" />
                    {t.socialPlatforms}
                  </Label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-pink-500" />
                      <Input
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        placeholder="Instagram"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    <div className="relative">
                      <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600" />
                      <Input
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleChange}
                        placeholder="Facebook"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    <div className="relative">
                      <TikTokIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
                      <Input
                        name="tiktok"
                        value={formData.tiktok}
                        onChange={handleChange}
                        placeholder="TikTok"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    <div className="relative">
                      <Twitch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-500" />
                      <Input
                        name="twitch"
                        value={formData.twitch}
                        onChange={handleChange}
                        placeholder="Twitch"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    <div className="relative sm:col-span-2">
                      <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-800" />
                      <Input
                        name="twitter_x"
                        value={formData.twitter_x}
                        onChange={handleChange}
                        placeholder="X (Twitter)"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">{t.message} <span className="text-gray-400 text-xs">({t.optional})</span></Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.messagePlaceholder}
                    rows={4}
                    className="rounded-xl resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl py-6 text-lg"
                  disabled={loading}
                  data-testid="affiliate-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      {tg('affiliates_submitting')}
                    </>
                  ) : (
                    <>
                      {t.submit}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AffiliatesPage;
